import { URGENT_KIND_LABELS, urgentBarText, type UrgentAction, type UrgentDoneRequest } from '@wms/domain';
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { api, errorMessage, type FloorAction } from '../api';
import { feedback, primeAudio, urgentBuzz, urgentSiren } from '../feedback';
import { fmt } from '../format';
import { store, useSession } from '../session';
import { alarmOf, arrivals, doneText, relabelFor, setUrgentMuted, urgentControl, urgentHint, urgentMuted } from '../urgent';
import { ChannelBadge, Empty, ErrorBanner, ScanField, SellerChip, Spinner, useFloorAction, useLoad } from '../ui';
import { Icon } from '../ui/icons';

/** The warehouse's open acil işler, as every floor screen shares them (one poll for the bar, the panel and Özet). */
interface UrgentState {
  readonly actions: readonly UrgentAction[] | undefined;
  readonly error: string | null;
  /** this device plays no sound for new ones */
  readonly muted: boolean;
  setMuted(muted: boolean): void;
  reload(): Promise<void>;
}

const UrgentContext = createContext<UrgentState | null>(null);

/** The open acil işler of the warehouse; null outside the warehouse's panel (a seller). */
export const useUrgent = (): UrgentState | null => useContext(UrgentContext);

/** The bar's poll: a carrier change reaches the floor's screens within this. */
const POLL_MS = 10_000;

/**
 * Around the warehouse's screens: the open acil işler, read every 10 seconds, and a short alarm (a siren,
 * unless this device was muted, and a buzz) for each one that arrives while a screen is open. Those already
 * open when the screen opened make no sound: the red bar shows them.
 */
export function UrgentProvider({ children }: { children: ReactNode }) {
  const { warehouseId } = useSession();
  const loaded = useLoad(() => api<UrgentAction[]>('GET', `/api/wms/floor-actions${warehouseId ? `?warehouseId=${warehouseId}` : ''}`), [warehouseId], POLL_MS);
  const [muted, setMutedState] = useState(() => urgentMuted(store));
  const mutedNow = useRef(muted);
  mutedNow.current = muted;
  // the ids seen so far; null: the next list is the first look (another warehouse's list is no arrival)
  const seen = useRef<Set<string> | null>(null);
  useEffect(() => {
    seen.current = null;
  }, [warehouseId]);
  useEffect(() => {
    if (!loaded.data) return;
    const { seen: next, fresh } = arrivals(seen.current, loaded.data);
    seen.current = next;
    const alarm = alarmOf(fresh.length, mutedNow.current);
    if (alarm.buzz) urgentBuzz();
    if (alarm.siren) urgentSiren();
  }, [loaded.data]);
  // a page is silent until touched: the first tap or key readies the alarm
  useEffect(() => {
    const ready = () => primeAudio();
    window.addEventListener('pointerdown', ready, { once: true });
    window.addEventListener('keydown', ready, { once: true });
    return () => {
      window.removeEventListener('pointerdown', ready);
      window.removeEventListener('keydown', ready);
    };
  }, []);
  const value: UrgentState = {
    actions: loaded.data,
    error: loaded.error,
    muted,
    setMuted: (next) => {
      setUrgentMuted(store, next);
      setMutedState(next);
    },
    reload: loaded.reload,
  };
  return <UrgentContext.Provider value={value}>{children}</UrgentContext.Provider>;
}

/** This device's sound for new acil işler, on and off. */
function MuteButton({ urgent, className }: { urgent: UrgentState; className: string }) {
  const label = urgent.muted ? 'Acil iş sesi bu cihazda kapalı: açmak için dokunun' : 'Acil iş sesi açık: bu cihazda kapatmak için dokunun';
  return (
    <button type="button" className={className} onClick={() => urgent.setMuted(!urgent.muted)} title={label} aria-label={label} aria-pressed={urgent.muted}>
      <Icon name={urgent.muted ? 'mute' : 'sound'} />
    </button>
  );
}

/**
 * Under the top bar of every warehouse screen, phones too, while acil işler are open: "⚠ ACİL:
 * 11760934092 — kargo değişti, etiketi değiştirin ve 2 iş daha". A tap opens the panel.
 */
export function UrgentBar({ section }: { section: string }) {
  const urgent = useUrgent();
  const text = urgentBarText(urgent?.actions ?? []);
  if (!urgent || !text) return null;
  return (
    <div className="urgent-bar no-print" role="alert">
      <a className="urgent-go" href="#/acil" aria-current={section === 'acil' ? 'page' : undefined}>
        <Icon name="alert" />
        <span className="urgent-text">{text}</span>
        {section !== 'acil' && <span className="urgent-open">Aç</span>}
      </a>
      <MuteButton urgent={urgent} className="urgent-mute" />
    </div>
  );
}

/** "az önce", "12 dk önce" */
const ago = (iso: string) => (fmt.age(iso) === '0 dk' ? 'az önce' : `${fmt.age(iso)} önce`);

/**
 * #/acil: every open acil iş with its whole instruction and its own way to close it (the new label's scan,
 * Rafa iade, "Etiketi attım", the packing desk), and what was closed in the last day, by whom and how.
 */
export function UrgentPanel() {
  const urgent = useUrgent();
  const { warehouseId, isSupervisor } = useSession();
  const recent = useLoad(() => api<UrgentAction[]>('GET', `/api/wms/floor-actions/done${warehouseId ? `?warehouseId=${warehouseId}` : ''}`), [warehouseId], 30_000);
  // "Etiketi attım", "Yaptım": a lost answer is sent again with its key, so it counts once
  const press = useFloorAction();
  const [answer, setAnswer] = useState<{ readonly ok: boolean; readonly text: string } | null>(null);
  // "Yaptım · okutmadan kapat" asks once more which relabel it closes
  const [confirming, setConfirming] = useState<string | null>(null);
  if (!urgent) return null;
  if (urgent.error && !urgent.actions) return <ErrorBanner message={urgent.error} onRetry={() => void urgent.reload()} />;
  if (!urgent.actions) return <Spinner />;
  const open = urgent.actions;
  // parcels packed before their carrier changed close on their new label's scan; an order not packed yet
  // closes at the desk that packs it (the desk has its old label taken off first)
  const relabels = open.filter((a) => a.kind === 'relabel' && a.orderStatus === 'packed');

  const finish = async (target: UrgentAction, code: string | null, action?: FloorAction) => {
    const body: UrgentDoneRequest = code ? { code } : {};
    try {
      const closed = await api<UrgentAction>('POST', `/api/wms/floor-actions/${target.id}/done`, body, { action });
      feedback('done');
      setAnswer({ ok: true, text: `${closed.orderNumber}: iş kapandı${closed.doneHow === 'scan' ? ', yeni etiket okutuldu' : ''}.` });
      setConfirming(null);
      void urgent.reload();
      void recent.reload();
    } catch (e) {
      feedback('error');
      setAnswer({ ok: false, text: errorMessage(e) });
    }
  };

  // the new label of a parcel whose carrier changed; an old one is refused by the server, in its words
  const onScan = async (code: string, action: FloorAction) => {
    const target = relabelFor(code, open);
    if (!target) {
      feedback('error');
      setAnswer({ ok: false, text: `${code}: açık bir “Kargo değişti” işinin etiketi değil.` });
      return;
    }
    await finish(target, code, action);
  };

  return (
    <>
      <section className="card">
        <div className="card-h">
          <Icon name="alert" />
          <h2>Açık acil işler</h2>
          <span className="sub">{open.length === 0 ? 'yok' : `${open.length} iş · en yenisi üstte`}</span>
          <span className="spacer" />
          <MuteButton urgent={urgent} className="icon-btn" />
        </div>
        {(relabels.length > 0 || answer || press.banner) && (
          <div className="card-b stack">
            {relabels.length > 0 && (
              <ScanField
                placeholder="Yeni kargo etiketini okutun"
                onScan={onScan}
                hint="Kargo firması değişen paketin yeni etiketini eskisinin üstüne yapıştırıp okutun: iş kapanır. Eski etiket reddedilir."
              />
            )}
            {answer && (
              <div className={`banner ${answer.ok ? 'ok' : 'bad'}`} role="status">
                <Icon name={answer.ok ? 'check' : 'alert'} />
                <div>{answer.text}</div>
              </div>
            )}
            {press.banner}
          </div>
        )}
      </section>

      {open.length === 0 ? (
        <Empty title="Açık acil iş yok">Kargo firması değişen ya da iptal edilen siparişler için yapılacak iş burada ve her ekranın üstünde kırmızı çubukta görünür.</Empty>
      ) : (
        open.map((a) => {
          const control = urgentControl(a);
          return (
            <section key={a.id} className="card urgent-card" aria-label={`${URGENT_KIND_LABELS[a.kind]}: ${a.orderNumber}`}>
              <div className="card-h">
                <span className="chip bad nowrap">{URGENT_KIND_LABELS[a.kind]}</span>
                <b className="mono urgent-order">{a.orderNumber}</b>
                <SellerChip code={a.tenantCode} />
                <ChannelBadge channel={a.channel} />
                <span className="spacer" />
                <span className="muted nowrap">{ago(a.createdAt)}</span>
              </div>
              <div className="card-b stack">
                <p className="urgent-instruction">{a.instruction}</p>
                <p className="muted urgent-hint">{urgentHint(a)}</p>
                <div className="row">
                  {control === 'discard' && (
                    <button className="btn primary" disabled={press.waiting} onClick={() => void press.run('Etiketi attım', (action) => finish(a, null, action))}>
                      <Icon name="trash" /> Etiketi attım
                    </button>
                  )}
                  {control === 'putaway' && (
                    <a className="btn primary" href="#/yerlestirme">
                      <Icon name="shelf" /> Yerleştirme → Rafa iade
                    </a>
                  )}
                  {(control === 'desk' || (control === 'scan' && a.orderStatus !== 'packed')) && (
                    <a className="btn" href={`#/paketleme/${a.orderId}`}>
                      <Icon name="box" /> Paketleme masasında aç
                    </a>
                  )}
                  {control === 'scan' && isSupervisor && confirming !== a.id && (
                    <button className="btn ghost" disabled={press.waiting} onClick={() => setConfirming(a.id)} title="Okuyucu yoksa: yeni etiketin yapıştırıldığını gözle kontrol edip kapatın">
                      <Icon name="check" /> Yaptım · okutmadan kapat
                    </button>
                  )}
                </div>
                {confirming === a.id && (
                  <div className="banner warn" role="alert">
                    <Icon name="alert" />
                    <div className="stack" style={{ flex: 1, gap: 8 }}>
                      <b>Eski etiket söküldü, yeni etiket {a.trackingNumber ? `(takip no ${a.trackingNumber}) ` : ''}yapıştırıldı mı?</b>
                      <div className="row">
                        <button className="btn primary" disabled={press.waiting} onClick={() => void press.run('Yaptım', (action) => finish(a, null, action))}>
                          Evet, kapat
                        </button>
                        <button className="btn ghost" onClick={() => setConfirming(null)}>
                          Vazgeç
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </section>
          );
        })
      )}

      <section className="card">
        <div className="card-h">
          <Icon name="check" />
          <h2>Son yapılanlar</h2>
          <span className="sub">son 24 saat</span>
        </div>
        {recent.error && !recent.data ? (
          <ErrorBanner message={recent.error} onRetry={() => void recent.reload()} />
        ) : !recent.data ? (
          <Spinner />
        ) : recent.data.length === 0 ? (
          <Empty title="Son 24 saatte kapanan acil iş yok" />
        ) : (
          <div className="table-wrap">
            <table className="t stack-sm">
              <tbody>
                {recent.data.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <span className="mono">{a.orderNumber}</span> <ChannelBadge channel={a.channel} short />
                    </td>
                    <td>
                      <SellerChip code={a.tenantCode} />
                    </td>
                    <td>{URGENT_KIND_LABELS[a.kind]}</td>
                    <td className="muted">{doneText(a)}</td>
                    <td className="muted nowrap">{a.doneAt ? fmt.dateTime(a.doneAt) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
